import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import PlotMap, { statusGroupOf } from '../../../shared/components/PlotMap';
import { getProjectLayout } from '../../services/api/implementation/impl';

const FACING_LABEL = {
  north: 'North',
  south: 'South',
  east: 'East',
  west: 'West',
  north_east: 'North-East',
  north_west: 'North-West',
  south_east: 'South-East',
  south_west: 'South-West'
};

const num = (n) => (n ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : null);

/** Layout tab: the project's active plot map, coloured by each unit's live status. */
const ProjectLayoutTab = ({ projectId }) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['projectLayout', projectId],
    queryFn: () => getProjectLayout(projectId),
    refetchOnWindowFocus: true
  });

  const plots = useMemo(
    () =>
      (data?.plots || []).map((p) => ({
        key: String(p.unitId),
        label: p.unitName,
        points: p.points,
        status: statusGroupOf(p.status),
        details: [
          ['Status', String(p.status || 'Vacant').replace(/_/g, ' ')],
          ['Area', num(p.area)],
          ['Built-up', num(p.buildUpArea)],
          ['Facing', FACING_LABEL[p.facing]]
        ].filter(([, v]) => v)
      })),
    [data]
  );

  if (isLoading) return <p style={{ padding: 16, color: '#64748b' }}>Loading layout…</p>;
  if (isError) {
    return (
      <div style={{ padding: 16 }}>
        <p style={{ color: '#b91c1c' }}>Could not load the layout.</p>
        <button onClick={() => refetch()}>Try again</button>
      </div>
    );
  }
  if (!data?.layout) {
    return (
      <div style={{ padding: 24, color: '#475569' }}>
        <b>No layout yet.</b> Once a master plan is imported for this project, its plots show here, coloured by
        availability.
      </div>
    );
  }

  const { layout } = data;
  return (
    <PlotMap
      layout={layout}
      plots={plots}
      embedded
      subtitle={
        data.unitsWithoutPlot
          ? `${data.unitsWithoutPlot} unit(s) of this project are not on the map.`
          : undefined
      }
    />
  );
};

export default ProjectLayoutTab;
